import type { Point, TooltipFormatterCallbackFunction } from 'highcharts';
import {
	buildComparisonStatusHtml,
	buildPreviousWithPercentHtml,
	formatComparisonPercentInParens,
	resolveComparisonDisplayState,
	resolveComparisonPolarityColor,
} from './comparison-display-format.util';
import { coalesceLineChartX } from './highcharts-datetime.util';
import {
	PORTAL_CHART_PREVIEW_DARK_INLINE,
	PORTAL_CHART_PREVIEW_LIGHT_INLINE,
} from './highcharts-portal-tooltip-theme-bridge';

export interface ChartTooltipHtmlTheme {
	background: string;
	textPrimary: string;
	textSecondary: string;
}

/** Inline colors for portaled `useHTML` tooltips (not `:root` CSS vars). */
export function chartTooltipHtmlTheme(
	mode: 'light' | 'dark'
): ChartTooltipHtmlTheme {
	const tokens =
		mode === 'dark'
			? PORTAL_CHART_PREVIEW_DARK_INLINE
			: PORTAL_CHART_PREVIEW_LIGHT_INLINE;
	return {
		background: tokens['--background-primary-color'],
		textPrimary: tokens['--text-primary-color'],
		textSecondary: tokens['--text-secondary-color'],
	};
}

const comparisonDateFormatter = new Intl.DateTimeFormat(undefined, {
	year: 'numeric',
	month: 'short',
	day: 'numeric',
});

export interface TooltipComparisonPoint {
	previousValue: number;
	periodLabel: string;
}

/** Index-aligned comparison value from `series.userOptions.comparisonData`. */
export function resolveTooltipComparisonPoint(
	comparisonData: unknown[] | undefined,
	pointIndex: number
): TooltipComparisonPoint | undefined {
	if (!comparisonData || !Array.isArray(comparisonData)) {
		return undefined;
	}

	const comparisonPoint = comparisonData[pointIndex];
	if (
		!comparisonPoint ||
		!Array.isArray(comparisonPoint) ||
		comparisonPoint.length < 2
	) {
		return undefined;
	}

	const previousValue = Number(comparisonPoint[1]);
	if (!Number.isFinite(previousValue)) {
		return undefined;
	}

	return {
		previousValue,
		periodLabel: formatComparisonPeriodLabel(comparisonPoint[0]),
	};
}

export function formatComparisonPeriodLabel(raw: unknown): string {
	if (raw === null || raw === undefined) {
		return 'previous period';
	}

	try {
		const c0 = raw as string | number;
		const coerced = coalesceLineChartX(
			typeof c0 === 'string' || typeof c0 === 'number' ? c0 : String(c0)
		);
		const ms =
			typeof coerced === 'number'
				? coerced
				: new Date(String(coerced)).getTime();
		if (Number.isFinite(ms) && !Number.isNaN(ms)) {
			return comparisonDateFormatter.format(new Date(ms));
		}
	} catch {
		// Non-date category keys (e.g. campaign names) use a generic label.
	}

	return 'previous period';
}

export function buildTooltipComparisonChangeHtml(
	currentValue: number,
	previousValue: number,
	inverseMetric?: boolean
): string {
	const state = resolveComparisonDisplayState(currentValue, previousValue);
	if (state.kind === 'new') {
		return buildComparisonStatusHtml('new');
	}
	if (state.kind === 'no-data') {
		return buildComparisonStatusHtml('no-data');
	}
	if (state.percentChange == null) {
		return '';
	}

	const color = resolveComparisonPolarityColor(
		state.percentChange,
		inverseMetric
	);
	const pctText = formatComparisonPercentInParens(state.percentChange, 0);

	return `<span style="color: ${color}">${pctText}</span>`;
}

export function buildTooltipComparisonSuffixHtml(
	currentValue: number,
	comparison: TooltipComparisonPoint,
	formatValue: (value: number) => string,
	inverseMetric?: boolean
): string {
	const state = resolveComparisonDisplayState(
		currentValue,
		comparison.previousValue
	);

	let inner = '';
	if (state.kind === 'new') {
		inner = buildComparisonStatusHtml('new');
	} else if (state.kind === 'no-data') {
		inner = buildComparisonStatusHtml('no-data');
	} else {
		inner = buildPreviousWithPercentHtml({
			previousFormatted: formatValue(state.displayPrevious),
			percentChange: state.percentChange ?? 0,
			decimals: 0,
			inverseMetric,
		});
	}

	return `
                                <span style="margin-left: 8px;">
                                    ${inner}
                                </span>
                            `;
}

function resolveTooltipComparisonForPoint(point: {
	index?: number;
	series: {
		userOptions?: {
			comparisonData?: unknown[];
			custom?: Record<string, unknown>;
		};
		options?: { custom?: Record<string, unknown> };
	};
}): TooltipComparisonPoint | undefined {
	const seriesOptions = point.series.userOptions || {};
	return resolveTooltipComparisonPoint(
		seriesOptions.comparisonData,
		point.index ?? 0
	);
}

function resolveTooltipInverseMetric(point: {
	series: {
		userOptions?: { custom?: Record<string, unknown> };
		options?: { custom?: Record<string, unknown> };
	};
}): boolean | undefined {
	const seriesOptions = point.series.userOptions || {};
	const customOptions =
		point.series.options?.custom || seriesOptions.custom || {};
	return customOptions.inverseMetric as boolean | undefined;
}

function formatCartesianTooltipValue(value: number): string {
	return value
		.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
		.replace(/\.00$/, '');
}

/** Shared bar/column category-axis tooltip with comparison suffix. */
export function createCartesianCategoryChartTooltipFormatter(
	formatCategoryLabel: (v: string | number) => string,
	pinnedThemeMode?: 'light' | 'dark'
): TooltipFormatterCallbackFunction {
	const theme = pinnedThemeMode
		? chartTooltipHtmlTheme(pinnedThemeMode)
		: null;
	const textSecondary =
		theme?.textSecondary ?? 'var(--text-secondary-color)';
	const textPrimary = theme?.textPrimary ?? 'var(--text-primary-color)';
	const background =
		theme?.background ?? 'var(--background-primary-color)';

	return function (this: { points?: Point[]; x?: number | string }) {
		const points = this.points || [];
		const xAxis = (
			points[0] as Point & {
				series?: { xAxis?: { categories?: string[] } };
			}
		)?.series?.xAxis;
		const categories = xAxis?.categories;
		const rawCategory =
			categories && typeof this.x === 'number'
				? categories[this.x]
				: this.x;
		const categoryDisplay =
			typeof rawCategory === 'string' || typeof rawCategory === 'number'
				? formatCategoryLabel(rawCategory)
				: String(rawCategory ?? '');
		let tooltipContent = `<div style="font-size: var(--font-size-xs); font-weight: var(--font-weight-regular); color: ${textSecondary}; margin-bottom: 5px;">${categoryDisplay}</div>`;

		const getBarStyle = (point: Point) =>
			`background-color: ${point.series.color}`;

		points.forEach((point: Point) => {
			const comparison = resolveTooltipComparisonForPoint(point);
			const comparisonHtml =
				comparison !== undefined
					? buildTooltipComparisonSuffixHtml(
							point.y ?? 0,
							comparison,
							formatCartesianTooltipValue,
							resolveTooltipInverseMetric(point)
						)
					: '';

			tooltipContent += `
                <div style="display: flex; align-items: center; margin-bottom: 5px;">
                    <div style="width: 3px; height: 30px; ${getBarStyle(
						point
					)}; margin-right: 8px;"></div>
                    <div style="display: flex; flex-direction: column;">
                        <span style="color: ${textSecondary}; font-size: var(--font-size-xs); font-weight: var(--font-weight-regular);">${
							point.series.name
						}</span>
                        <div style="color: ${textPrimary}; font-weight: var(--font-weight-regular); font-size: var(--font-size-sm)">
                            ${formatCartesianTooltipValue(point.y ?? 0)}${comparisonHtml}
                        </div>
                    </div>
                </div>`;
		});

		return `<div style="padding: 5px; background: ${background};">${tooltipContent}</div>`;
	};
}
